import { useState } from "react";
import {
  BackImg,
  Bulb,
  GreenTick,
  NoSleepCoach,
  WhiteMoon,
} from "@utils/defaultImages";
import { toast } from "react-toastify";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useTranslation } from "react-i18next";
import SleepCoachDoneDialogue from "@features/sleep/SleepCoachDoneDialogue";
import { markSleepAsDone } from "api/sleepCoach";
import { useNavigate } from "react-router-dom";
import EmptyPlanView from "@components/ui/EmptyPlanView";
import { getSleepCoachStepsData } from "data/uiData/CardsData/sleepData";
import OpenDescriptionCard from "@components/ui/OpenDescriptionCard";
import SleepOpenCards from "@features/sleep/SleepOpenCards";

const SingleDaySleepCoach = ({ sleepAdvice }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkedAsDone, setIsMarkedAsDone] = useState(
    sleepAdvice?.mark_as_done === 1 || false
  );
  const [wakeups, setWakeups] = useState(sleepAdvice?.per_night_wake_ups);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const adviceDate = sleepAdvice?.date;
  const canMarkAsDone = adviceDate && adviceDate <= today;

  const handleMarkDone = async (data) => {
    if (!sleepAdvice || !data) return;

    setIsLoading(true);

    try {
      const response = await markSleepAsDone({
        date: sleepAdvice.date,
        wake_up: data.wake_up,
        avg_sleep_duration: data.avg_sleep_duration,
      });

      const wakUpCal = response?.data?.data?.advice?.find(
        (item) => item?.date === adviceDate
      );
      setWakeups(wakUpCal?.per_night_wake_ups);
      // Optimistic update
      setIsMarkedAsDone(true);

      toast.info(t("toast.statusUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(t("toast.failUpdated"));

      // Rollback if API fails
      setIsMarkedAsDone(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (data = null) => {
    if (data) {
      handleMarkDone(data);
      setOpen(false);
    } else {
      setOpen(false);
    }
  };

  if (!sleepAdvice) {
    return (
      <EmptyPlanView
        IconComponent={<NoSleepCoach />}
        heading={t("dashboard.noSleepCoachHeading")}
        description={t("dashboard.noSleepCoachDescription")}
      />
    );
  }

  const stepsData = getSleepCoachStepsData({
    sleepCoachStepsData: sleepAdvice?.description_steps,
    t,
  });

  return (
    <>
      <div
        className="bg-cover shadow-lg p-6 rounded-lg w-full max-w-full"
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-[600] text-[#fff] text-[16px] font-[Lato]">
            {t("Your Today's Sleep Advice")}
          </h2>
          <button
            className="bg-white rounded-full text-[#0575e6] text-[12px] font-[Lato] font-[600] py-2 px-3"
            onClick={() => navigate("/sleep-coach")}
          >
            {t("dashboard.View Details")}
          </button>
        </div>
        <div className="border rounded-[17px] p-2 md:p-4 bg-white font-[Lato]">
          <div className="flex flex-col md:flex-row justify-between gap-2 mb-5">
            <div className="flex gap-2 md:items-center flex-col md:flex-row">
              <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                  <Bulb />
                </p>
                <span>
                  {new Date(sleepAdvice?.date).toDateString() ===
                  new Date().toDateString()
                    ? t("TodayAdvice")
                    : `${t(`days.${sleepAdvice?.day?.toLowerCase()}`)} ’s ${t(
                        "mindfulnessCoachPreferences.Practice"
                      )}`}
                </span>
              </button>
              {wakeups && (
                <>
                  <button className="text-[#fff] cursor-default text-[12px] md:text-[16px] font-[600] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                    <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                      <WhiteMoon />
                    </p>{" "}
                    {t("sleepCoachPreferences.Nighttime awakenings")} :{" "}
                    {t(`sleepCoachPreferences.${wakeups}`)}
                  </button>
                </>
              )}
            </div>
            {canMarkAsDone &&
              (isMarkedAsDone ? (
                <button className="text-[#fff] cursor-default text-[12px] md:text-[14px] border border-[#36F136] rounded-full px-2 py-1 flex items-center gap-2 w-[fit-content]">
                  <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                    <GreenTick />
                  </p>
                </button>
              ) : (
                <button
                  className="text-[#fff] cursor-pointer text-[12px] md:text-[14px] font-[600] bg-[#36F136] rounded-full px-2 py-1 flex items-center justify-center gap-2 min-w-[136px] min-h-[36px] w-[fit-content] "
                  onClick={() => setOpen(true)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <ButtonLoader />
                  ) : (
                    <>
                      <p className="bg-white rounded-full w-7 h-7 flex items-center justify-center">
                        <GreenTick />
                      </p>
                      <span>{t("sleepCoachPreferences.mark as done")}</span>
                    </>
                  )}
                </button>
              ))}
          </div>

          <p className="text-[#000] font-[600] text-[18px] mb-3">
            {sleepAdvice?.title}
          </p>

          <p className="text-[#7B7B7B] text-[18px]">
            {sleepAdvice?.description}
          </p>
          {sleepAdvice?.description_steps && (
            // <OpenDescriptionCard
            //   stepsData={stepsData}
            // />
            <SleepOpenCards stepsData={stepsData} />
          )}
        </div>
      </div>

      <SleepCoachDoneDialogue
        open={open}
        handleClose={handleClose}
        isLoading={isLoading}
        heading={t("sleepCoachPreferences.Daily Sleep Coach Update")}
        message={t(
          "sleepCoachPreferences.Track your average sleep duration and whether you woke up on time today."
        )}
      />
    </>
  );
};

export default SingleDaySleepCoach;
